<template>
  <header class="news-header">
    <div class="news-header__container">
      <div class="news-header__left">
        <!-- Mobile Menu Toggle -->
        <button
          v-if="showMobileMenu"
          @click="$emit('toggle-mobile-menu')"
          class="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>

        <router-link to="/news" class="news-header__brand">
          <div class="brand-icon">
            <img src="/assets/img/devlusaja_logo.png" alt="devlusaja logo" width="50" height="50">
          </div>
          <h1 class="news-header__title">DevNews</h1>
          <span class="news-header__subtitle">{{ subtitle }}</span>
        </router-link>
      </div>

      <div class="news-header__controls">
        <div v-if="showSearch" class="search-container">
          <div class="search-input-wrapper">
            <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              ref="searchInput"
              :value="searchQuery"
              @input="$emit('update:searchQuery', $event.target.value)"
              @keydown.esc="clearSearch"
              type="text"
              placeholder="Buscar..."
              aria-label="Buscar"
              class="search-input"
              :class="{'search-input--filled': searchQuery}"
            />
            <!-- Limpiar búsqueda / atajo de teclado -->
            <button
              v-if="searchQuery"
              class="search-clear"
              aria-label="Limpiar búsqueda"
              @click="clearSearch"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <kbd v-else class="search-kbd" title="Presiona / para buscar">/</kbd>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'HeaderNews',
  props: {
    subtitle: {
      type: String,
      default: 'Noticias y contenido sobre Inteligencia Artificial'
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    searchQuery: {
      type: String,
      default: ''
    },
    showMobileMenu: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    clearSearch() {
      this.$emit('update:searchQuery', '');
      this.$refs.searchInput.blur();
    },
    // Atajo "/" para enfocar el buscador (si no se está escribiendo en otro campo)
    onKeydown(e) {
      if (e.key !== '/' || !this.$refs.searchInput) return;
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName) || e.target.isContentEditable) return;
      e.preventDefault();
      this.$refs.searchInput.focus();
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
  }
};
</script>

<style scoped>
/* Dark Theme - Header */
.news-header {
  position: sticky;
  top: 0;
  background: rgba(20, 25, 31, 0.98);
  border-bottom: 1px solid #2d333b;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  width: 100%;
}

.news-header__container {
  height: 56px; /* + 1px de borde = 57px, usado como offset en Sidebar y vistas */
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.news-header__left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mobile-menu-btn {
  display: none;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid #2d333b;
  border-radius: 8px;
  color: #a0aec0;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.mobile-menu-btn:hover {
  background: rgba(74, 158, 255, 0.1);
  border-color: #4a9eff;
  color: #4a9eff;
}

.news-header__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.news-header__brand:hover {
  opacity: 0.8;
}

.brand-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 12px;
}

.brand-icon img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.news-header__title {
  font-size: 20px;
  font-weight: 800;
  color: #e1e7ef;
  margin: 0;
  background: linear-gradient(135deg, #4a9eff, #7b68ee);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.news-header__subtitle {
  font-size: 14px;
  color: #7d8694;
  font-weight: 500;
}

.news-header__controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.search-container {
  position: relative;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 11px;
  color: #7d8694;
  z-index: 1;
  pointer-events: none;
  transition: color 0.2s ease;
}

.search-input-wrapper:focus-within .search-icon {
  color: #4a9eff;
}

/* Compacto en reposo; se ensancha al enfocar o con texto */
.search-input {
  box-sizing: border-box;
  height: 34px;
  padding: 0 32px 0 34px;
  border: 1px solid #2d333b;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  width: 220px;
  background: #1c2127;
  color: #e1e7ef;
  transition: width 0.25s ease, border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.search-input:hover {
  border-color: #4a5568;
}

.search-input:focus,
.search-input--filled {
  width: 320px;
}

.search-kbd,
.search-clear {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  min-width: 20px;
  border-radius: 4px;
}

.search-kbd {
  padding: 0 5px;
  box-sizing: border-box;
  border: 1px solid #2d333b;
  background: #262c34;
  color: #7d8694;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  pointer-events: none;
}

.search-input:focus ~ .search-kbd {
  display: none;
}

.search-clear {
  padding: 0;
  border: none;
  background: transparent;
  color: #7d8694;
  cursor: pointer;
}

.search-clear:hover {
  background: #2d333b;
  color: #e1e7ef;
}

.search-input::placeholder {
  color: #7d8694;
}

.search-input:focus {
  outline: none;
  border-color: #4a9eff;
  box-shadow: 0 0 0 3px rgba(74, 158, 255, 0.15);
  background: #262c34;
}

@media (max-width: 1024px) {
  .news-header__subtitle {
    display: none;
  }

  .search-input {
    width: 180px;
  }
  .search-input:focus,
  .search-input--filled {
    width: 260px;
  }
}

@media (max-width: 768px) {
  .news-header__container {
    padding: 0 16px;
  }

  .mobile-menu-btn {
    display: flex;
  }

  .news-header__brand {
    gap: 8px;
  }

  .brand-icon {
    width: 32px;
    height: 32px;
  }

  .brand-icon img {
    width: 32px;
    height: 32px;
  }

  .news-header__title {
    font-size: 18px;
  }

  .news-header__subtitle {
    display: none;
  }

  .search-input {
    width: 150px;
    font-size: 16px; /* <16px hace que iOS haga zoom al enfocar */
  }
  .search-input:focus,
  .search-input--filled {
    width: 200px;
  }
  .search-kbd {
    display: none; /* sin teclado físico en móvil */
  }
}

@media (max-width: 480px) {
  .news-header__container {
    padding: 0 12px;
  }

  .brand-icon {
    width: 28px;
    height: 28px;
  }

  .brand-icon img {
    width: 28px;
    height: 28px;
  }

  .news-header__title {
    font-size: 16px;
  }

  .search-input {
    width: 120px;
  }
  .search-input:focus,
  .search-input--filled {
    width: 160px;
  }
}
</style>
